// Page "Factures" (Lot 3A) : liste filtrable avec colorimétrie d'échéance
// et actions rapides (ouvrir, marquer encaissée).
//
// Filtres exposés (synchronisés avec la query-string pour URL partageable) :
//   - status (multi : issued, overdue, partial, paid, cancelled, draft)
//   - client, projet, intervenant (dropdowns)
//   - période d'émission (from / to)
//   - recherche plein texte sur la référence
//
// Actions inline (colonne de droite) :
//   - Ouvrir → /invoices/:id
//   - Marquer encaissée → POST /invoices/:id/payments (montant = remaining)

import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { clientsApi } from "../api/clients";
import { invoicesApi } from "../api/invoices";
import { intervenantsApi } from "../api/intervenants";
import { projectsApi } from "../api/projects";
import type {
  ClientPublic,
  IntervenantPublic,
  InvoiceFilters,
  InvoiceListItem,
  InvoiceListResponse,
  InvoiceStatus,
  ProjectPublic,
  UUID,
} from "../api/types";
import { AppShell } from "../components/AppShell";
import { formatAmount } from "../utils/currency";
import { Modal } from "../components/Modal";
import { DueBadge } from "../components/DueBadge";

const PAGE_SIZE = 50;

// STATUS_LABELS is now built dynamically via t() inside the component

const STATUS_ORDER: InvoiceStatus[] = [
  "issued",
  "overdue",
  "partial",
  "paid",
  "cancelled",
  "draft",
];

function formatDateFR(iso: string): string {
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ---- Extraction des filtres depuis l'URL -----------------------------------

function parseFiltersFromSearch(
  params: URLSearchParams,
): InvoiceFilters {
  const statuses = params.getAll("status").filter((s): s is InvoiceStatus =>
    STATUS_ORDER.includes(s as InvoiceStatus),
  );
  const offsetRaw = params.get("offset");
  const offset = offsetRaw ? Math.max(0, parseInt(offsetRaw, 10) || 0) : 0;
  return {
    status: statuses.length ? statuses : undefined,
    client_id: params.get("client_id") || undefined,
    project_id: params.get("project_id") || undefined,
    intervenant_id: params.get("intervenant_id") || undefined,
    issue_date_from: params.get("issue_date_from") || undefined,
    issue_date_to: params.get("issue_date_to") || undefined,
    q: params.get("q") || undefined,
    offset,
    limit: PAGE_SIZE,
  };
}

function filtersToSearch(filters: InvoiceFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (filters.status) for (const s of filters.status) p.append("status", s);
  if (filters.client_id) p.set("client_id", filters.client_id);
  if (filters.project_id) p.set("project_id", filters.project_id);
  if (filters.intervenant_id) p.set("intervenant_id", filters.intervenant_id);
  if (filters.issue_date_from) p.set("issue_date_from", filters.issue_date_from);
  if (filters.issue_date_to) p.set("issue_date_to", filters.issue_date_to);
  if (filters.q) p.set("q", filters.q);
  if (filters.offset) p.set("offset", String(filters.offset));
  return p;
}

// ---------------------------------------------------------------------------

export function InvoicesPage() {
  const { t } = useTranslation();

  const STATUS_LABELS: Record<InvoiceStatus, string> = {
    draft: t("status.draft"),
    issued: t("status.issued"),
    partial: t("status.partial"),
    paid: t("status.paid"),
    overdue: t("status.overdue"),
    cancelled: t("status.cancelled"),
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => parseFiltersFromSearch(searchParams),
    [searchParams],
  );

  const [list, setList] = useState<InvoiceListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Dropdowns de filtres — chargés une seule fois
  const [clients, setClients] = useState<ClientPublic[]>([]);
  const [projects, setProjects] = useState<ProjectPublic[]>([]);
  const [intervenants, setIntervenants] = useState<IntervenantPublic[]>([]);

  // Champ "recherche texte" — on ne l'applique qu'au submit pour éviter
  // un reload par frappe.
  const [qDraft, setQDraft] = useState<string>(filters.q ?? "");
  useEffect(() => setQDraft(filters.q ?? ""), [filters.q]);

  useEffect(() => {
    // Charge les dropdowns (clients / projets / intervenants) une fois.
    Promise.all([
      clientsApi.list().catch(() => []),
      projectsApi.list().catch(() => []),
      intervenantsApi.list().catch(() => []),
    ]).then(([c, p, i]) => {
      setClients(c);
      setProjects(p);
      setIntervenants(i);
    });
  }, []);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const data = await invoicesApi.list(filters);
      setList(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur de chargement");
      setList({ total: 0, limit: PAGE_SIZE, offset: 0, items: [] });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  function updateFilters(patch: Partial<InvoiceFilters>) {
    // Toute modification de filtre remet la pagination à 0 — sauf si le
    // patch porte explicitement `offset` (cas des boutons Précédent/Suivant).
    const next: InvoiceFilters = {
      ...filters,
      ...patch,
      offset: "offset" in patch ? patch.offset : 0,
    };
    // Nettoie les clés undefined pour ne pas les mettre en query-string
    setSearchParams(filtersToSearch(next));
  }

  function onSubmitSearch(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    updateFilters({ q: qDraft || undefined });
  }

  function toggleStatus(s: InvoiceStatus) {
    const cur = new Set(filters.status ?? []);
    if (cur.has(s)) cur.delete(s);
    else cur.add(s);
    updateFilters({ status: cur.size ? Array.from(cur) : undefined });
  }

  function resetFilters() {
    setSearchParams(new URLSearchParams());
  }

  // ---- Export CSV/XLSX -----------------------------------------------------
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState<InvoiceListItem | null>(null);
  const exportBtnRef = useRef<HTMLDivElement>(null);

  // Ferme le menu quand on clique en-dehors (UX standard des dropdowns)
  useEffect(() => {
    if (!exportOpen) return;
    function onDown(e: MouseEvent) {
      if (!exportBtnRef.current) return;
      if (!exportBtnRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    }
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [exportOpen]);

  async function triggerExport(format: "csv" | "xlsx") {
    setExportOpen(false);
    setExporting(true);
    try {
      const { blob, filename } = await invoicesApi.exportAs(format, filters);
      // Déclenche le téléchargement via un <a> fantôme — pattern standard
      // quand la requête nécessite des headers (Authorization) et qu'on ne
      // peut donc pas utiliser un <a href> direct.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `factures.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.detail : "Export impossible";
      window.alert(msg);
    } finally {
      setExporting(false);
    }
  }

  function onMarkPaid(item: InvoiceListItem) {
    setMarkPaidTarget(item);
  }

  async function confirmMarkPaid(paymentDate: string) {
    if (!markPaidTarget) return;
    try {
      await invoicesApi.addPayment(markPaidTarget.id, { payment_date: paymentDate });
      setMarkPaidTarget(null);
      await reload();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Action impossible";
      window.alert(msg);
    }
  }

  async function onDelete(item: InvoiceListItem) {
    if (
      !window.confirm(
        'Supprimer définitivement la facture ' + item.reference + ' ? Cette action est irréversible.',
      )
    ) {
      return;
    }
    try {
      await invoicesApi.delete(item.id);
      await reload();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Suppression impossible";
      window.alert(msg);
    }
  }

  const total = list?.total ?? 0;
  const pageStart = filters.offset ?? 0;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, total);

  return (
    <AppShell>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("invoicesPage.title")}</h1>
          <p className="text-sm text-slate-500">
            {t("invoicesPage.subtitle")}</p>
        </div>
        <div className="relative" ref={exportBtnRef}>
          <button
            type="button"
            onClick={() => setExportOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={exportOpen}
            disabled={exporting}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {exporting ? t("invoicesPage.exporting") : t("invoicesPage.exportXlsx")}
            <span aria-hidden className="text-slate-400">▾</span>
          </button>
          {exportOpen && (
            <div
              role="menu"
              aria-label="Format d'export"
              className="absolute right-0 mt-1 w-44 rounded-md border border-slate-200 bg-white shadow-lg z-10"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => void triggerExport("csv")}
                className="block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                CSV (.csv)
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => void triggerExport("xlsx")}
                className="block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Excel (.xlsx)
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Filtres */}
      <section
        aria-label="Filtres"
        className="mb-4 bg-white rounded-xl border border-slate-200 p-4 space-y-3"
      >
        <div className="flex flex-wrap gap-2">
          {STATUS_ORDER.map((s) => {
            const active = (filters.status ?? []).includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                aria-pressed={active}
                className={[
                  "px-3 py-1 rounded-full text-xs font-medium border",
                  active
                    ? "bg-brand-500 text-white border-brand-500"
                    : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100",
                ].join(" ")}
              >
                {STATUS_LABELS[s]}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3">
          <label className="text-xs text-slate-500 flex flex-col gap-1 min-w-[160px] flex-1">
            {t("common.client")}
            <select
              value={filters.client_id ?? ""}
              onChange={(e) =>
                updateFilters({ client_id: e.target.value || undefined })
              }
              className="rounded-md border border-slate-200 px-2 py-1.5 text-sm"
            >
              <option value="">{t("invoicesPage.allClients")}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs text-slate-500 flex flex-col gap-1 min-w-[160px] flex-1">
            {t("common.project")}
            <select
              value={filters.project_id ?? ""}
              onChange={(e) =>
                updateFilters({ project_id: e.target.value || undefined })
              }
              className="rounded-md border border-slate-200 px-2 py-1.5 text-sm"
            >
              <option value="">{t("invoicesPage.allProjects")}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs text-slate-500 flex flex-col gap-1 min-w-[160px] flex-1">
            {t("common.intervenant")}
            <select
              value={filters.intervenant_id ?? ""}
              onChange={(e) =>
                updateFilters({ intervenant_id: e.target.value || undefined })
              }
              className="rounded-md border border-slate-200 px-2 py-1.5 text-sm"
            >
              <option value="">{t("invoicesPage.allIntervenants")}</option>
              {intervenants.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </label>

          <div className="text-xs text-slate-500 flex flex-col gap-1 min-w-[200px] flex-1">
            {t("invoicesPage.issuePeriod")}
            <div className="flex flex-wrap gap-2">
              <input
                type="date"
                value={filters.issue_date_from ?? ""}
                onChange={(e) =>
                  updateFilters({
                    issue_date_from: e.target.value || undefined,
                  })
                }
                className="rounded-md border border-slate-200 px-2 py-1.5 text-sm w-full"
                aria-label={t("invoicesPage.colIssued")}
              />
              <input
                type="date"
                value={filters.issue_date_to ?? ""}
                onChange={(e) =>
                  updateFilters({
                    issue_date_to: e.target.value || undefined,
                  })
                }
                className="rounded-md border border-slate-200 px-2 py-1.5 text-sm w-full"
                aria-label={t("invoicesPage.colIssued")}
              />
            </div>
          </div>
        </div>

        <form
          role="search"
          aria-label={t("invoicesPage.searchPlaceholder")}
          onSubmit={onSubmitSearch}
          className="flex gap-2 items-center"
        >
          <input
            type="search"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            placeholder={t("invoicesPage.searchPlaceholder")}
            className="flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm"
          />
          <button type="submit" className="btn-primary">
            {t("invoicesPage.search")}
          </button>
          <button
            type="button"
            className="text-sm text-slate-500 hover:underline"
            onClick={resetFilters}
          >
            {t("invoicesPage.reset")}
          </button>
        </form>
      </section>

      {/* Résultats */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {error && (
          <p role="alert" className="p-4 text-sm text-red-600">
            {error}
          </p>
        )}
        {loading && list === null && (
          <p className="p-6 text-sm text-slate-500">{t("invoicesPage.loadingList")}</p>
        )}
        {list !== null && list.items.length === 0 && !error && (
          <p className="p-6 text-sm text-slate-500">
            {t("invoicesPage.noMatch")}
          </p>
        )}
        {list !== null && list.items.length > 0 && (
          <table className="w-full text-sm" aria-label="Liste des factures">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">{t("invoicesPage.colRef")}</th>
                <th className="px-4 py-2 font-medium">{t("invoicesPage.colClient")}</th>
                <th className="px-4 py-2 font-medium">{t("invoicesPage.colIssued")}</th>
                <th className="px-4 py-2 font-medium">{t("invoicesPage.colDue")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("invoicesPage.colTTC")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("invoicesPage.colReste")}</th>
                <th className="px-4 py-2 font-medium">{t("common.status")}</th>
                <th className="px-4 py-2 font-medium text-right w-44">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((it) => (
                <tr key={it.id} className={`border-t border-slate-100${it.effective_status === "draft" && (!it.project_id || !it.intervenant_id) ? " bg-amber-50" : ""}`}>
                  <td className="px-4 py-2 font-medium text-slate-900">
                    <Link
                      to={`/invoices/${it.id}`}
                      className="hover:underline text-brand-600"
                    >
                      {it.reference}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-slate-700">{it.client_name}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {formatDateFR(it.issue_date)}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">
                        {formatDateFR(it.due_date)}
                      </span>
                      <DueBadge
                        dueDate={it.due_date}
                        effectiveStatus={it.effective_status}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right text-slate-800">
                    {formatAmount(it.amount_ttc, it.currency)}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-800">
                    {formatAmount(it.remaining, it.currency)}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {STATUS_LABELS[it.effective_status]}
                    {it.effective_status === "draft" && (!it.project_id || !it.intervenant_id) && (
                      <span className="ml-2 text-xs font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                        ⚠ À compléter
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right space-x-3">
                    <Link
                      to={`/invoices/${it.id}`}
                      className="text-brand-600 hover:underline"
                    >
                      Ouvrir
                    </Link>
                    <button
                          type="button"
                          onClick={() => void onDelete(it)}
                          className="text-red-500 hover:underline"
                        >
                          Supprimer
                        </button>
                    {it.effective_status !== "paid" &&
                      it.effective_status !== "cancelled" &&
                      it.effective_status !== "draft" && (
                        <button
                          type="button"
                          onClick={() => void onMarkPaid(it)}
                          className="text-brand-600 hover:underline"
                        >
                          {t("invoicesPage.markPaid")}
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {list !== null && list.total > PAGE_SIZE && (
        <div
          className="mt-4 flex items-center justify-between text-sm text-slate-600"
          aria-label="Pagination"
        >
          <p>
            {pageStart + 1}–{pageEnd} sur {total}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="px-3 py-1 rounded-md border border-slate-200 disabled:opacity-40"
              disabled={pageStart === 0}
              onClick={() =>
                updateFilters({
                  offset: Math.max(0, pageStart - PAGE_SIZE),
                })
              }
            >
              {t("invoicesPage.previous")}
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded-md border border-slate-200 disabled:opacity-40"
              disabled={pageEnd >= total}
              onClick={() =>
                updateFilters({ offset: pageStart + PAGE_SIZE })
              }
            >
              {t("invoicesPage.next")}
            </button>
          </div>
        </div>
      )}
      {/* Modale date de paiement */}
      <MarkPaidModal
        invoice={markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        onConfirm={confirmMarkPaid}
      />
    </AppShell>
  );
}

// ---- Modale "Marquer payée" avec date ------------------------------------

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function MarkPaidModal({
  invoice,
  onClose,
  onConfirm,
}: {
  invoice: InvoiceListItem | null;
  onClose: () => void;
  onConfirm: (date: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayISO());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (invoice) { setDate(todayISO()); setError(null); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id]);

  if (!invoice) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onConfirm(date);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  const amountFmt = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: invoice.currency,
  }).format(Number(invoice.amount_ttc));

  return (
    <Modal open={true} onClose={onClose} title={t("invoicesPage.markPaidTitle")}>
      <p className="text-sm text-slate-600 mb-4">
        Facture{" "}
        <span className="font-semibold">{invoice.reference}</span>
        {" "}&mdash;{" "}
        <span className="font-semibold">{amountFmt}</span>
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="payment_date"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            {t("invoicesPage.paymentDate")}
          </label>
          <input
            id="payment_date"
            type="date"
            required
            value={date}
            max={todayISO()}
            onChange={(e) => setDate(e.target.value)}
            className="input w-full"
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-sm text-slate-500 hover:text-slate-800"
          >
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? t("common.saving") : t("invoicesPage.markPaidConfirm")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
